import React, { useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { channelManagerText } from '../i18n/pages/channelManager';

const BOOKING_GUIDE_KEY = 'hostex_booking_guide_expanded';

/**
 * The one setup step that happens outside Guestbook and silently breaks the integration when
 * skipped: whitelisting our domain on Booking.com so the form link is not stripped out of guest
 * messages. (Webhook registration used to be a manual walkthrough here too, but it now happens
 * automatically when the connection is made — see PmsConnectionService.registerWebhook.)
 *
 * Collapsible and remembers its state, because a manager reads it once during setup and then
 * wants it out of the way.
 */
const HostexSetupGuide: React.FC = () => {
  const { lang } = useLang();
  const t = channelManagerText[lang];

  const [bookingOpen, setBookingOpen] = useState(
    () => localStorage.getItem(BOOKING_GUIDE_KEY) === 'true'
  );

  const toggleBooking = () => {
    const next = !bookingOpen;
    setBookingOpen(next);
    localStorage.setItem(BOOKING_GUIDE_KEY, String(next));
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    background: 'none',
    border: 'none',
    padding: '10px 0',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '0.92rem',
    fontWeight: 600,
    color: '#2d3748',
  };

  const stepStyle: React.CSSProperties = {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    fontSize: '0.87rem',
    color: '#4a5568',
    padding: '5px 0',
  };

  const numStyle: React.CSSProperties = {
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#e2e8f0',
    color: '#4a5568',
    fontSize: '0.72rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  };

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
      {/* Booking.com link whitelisting, framed as the question a manager would actually search for */}
      <button type="button" style={headerStyle} onClick={toggleBooking} aria-expanded={bookingOpen}>
        <span>❓ {t.guideBookingTitle}</span>
        <span style={{ color: '#a0aec0' }}>{bookingOpen ? '▲' : '▼'}</span>
      </button>
      {bookingOpen && (
        <div style={{ paddingBottom: 14 }}>
          <div
            style={{
              display: 'flex',
              gap: 9,
              alignItems: 'flex-start',
              padding: '10px 12px',
              margin: '4px 0 10px',
              background: '#fef9c3',
              border: '1px solid #fde047',
              borderRadius: 8,
              fontSize: '0.85rem',
              color: '#713f12',
            }}
          >
            <span>⚠️</span>
            <span>{t.guideBookingWhy}</span>
          </div>
          <div style={stepStyle}>
            <span style={numStyle}>1</span>
            <span>{t.guideBookingStep1}</span>
          </div>
          <div style={stepStyle}>
            <span style={numStyle}>2</span>
            <span>{t.guideBookingStep2}</span>
          </div>
          <div style={stepStyle}>
            <span style={numStyle}>3</span>
            <span>{t.guideBookingStep3}</span>
          </div>
          <div style={{ ...stepStyle, marginTop: 6, color: '#718096', fontStyle: 'italic' }}>
            <span style={{ ...numStyle, background: '#f0fdf4', color: '#166534' }}>✓</span>
            <span>{t.guideAirbnbNote}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostexSetupGuide;
